import React from 'react';
import styled from 'styled-components';
import { Heading3, P, Card, Margin, theme } from '@ovotech/element-v5';
import { breakpoints, breakpointVals } from '@/src/utils/mediaQuery';
import { ReactComponent as AppleStoreBadge } from '@/src/resources/apple-store-badge.svg';
import { ReactComponent as GooglePlayBadge } from '@/src/resources/google-play-badge-2026.svg';
import QRCode from '@/src/resources/get-app-qr.png';
import BeyondAppImage from './BeyondApp.png';
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
} from '@/src/pages/AllRewards/components/constants';
import { useBeyondAccountFlags } from '@/src/hooks/useBeyondAccountFlags/useBeyondAccountFlags';

const Container = styled(Card)`
  padding: 35px 0px 0 40px;
  border-radius: 4px;
  overflow: hidden;

  @media ${breakpoints.smallDown} {
    padding: 32px 0 0 24px;
  }
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    'header image'
    'content image';
  column-gap: 24px;

  @media ${breakpoints.smallDown} {
    grid-template-columns: 1fr 162px;
    grid-template-areas:
      'header header'
      'content image';
    column-gap: 16px;
  }
`;

const HeaderArea = styled.div`
  grid-area: header;
  margin-bottom: 24px;

  @media ${breakpoints.smallDown} {
    margin-bottom: 32px;
  }
`;

const ContentArea = styled.div`
  grid-area: content;
  display: flex;
  flex-direction: column;
  padding-bottom: 35px;

  @media ${breakpoints.smallDown} {
    padding-bottom: 32px;
  }
`;

const ImageArea = styled.div`
  grid-area: image;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
`;

const AppBadgesContainer = styled.div`
  display: flex;
  position: relative;
  gap: ${theme.space['300']};
  align-items: flex-end;
  margin-top: auto;
  padding-top: 32px;

  @media ${breakpoints.mediumDown} {
    flex-direction: column;
  }
`;

const AppBadge = styled.a`
  display: flex;
  height: 44px;

  z-index: 1;

  svg {
    height: 100%;
    width: auto;
  }

  @media ${breakpoints.mediumDown} {
    height: auto;
    width: 150px;

    svg {
      width: 100%;
      height: auto;
    }
  }
`;

const QRCodeWrapper = styled.div`
  align-self: flex-start;
  margin-left: 33px;

  @media (min-width: ${breakpointVals.navigation}px) and (max-width: 1075px) {
    margin-left: 0;
  }

  @media (min-width: ${breakpointVals.medium}px) and (max-width: 820px) {
    margin-left: 0;
  }

  @media ${breakpoints.mediumDown} {
    display: none;
  }
`;

const AppImage = styled.img`
  max-width: 310px;
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
  object-position: bottom right;

  @media ${breakpoints.smallDown} {
    width: 162px;
    height: 244px;

    object-fit: cover;
    object-position: left top;
  }
`;

const Heading = styled(Heading3)`
  color: ${({ theme }) => theme.colors.brand.midnight};
`;

type GetAppProps = {
  onAppStoreClick: () => void;
  onGooglePlayClick: () => void;
};

export const GetApp = ({ onAppStoreClick, onGooglePlayClick }: GetAppProps) => {
  const { isActivated } = useBeyondAccountFlags();

  const bodyText = isActivated
    ? "It's free to download. Already got it? Open the app to see your rewards."
    : "It's free to download. Beyond unlocks automatically once you're eligible.";

  return (
    <Container>
      <LayoutGrid>
        <HeaderArea>
          <Heading>Get the OVO app</Heading>
          <Margin top={2}>
            <P size="md">{bodyText}</P>
          </Margin>
        </HeaderArea>

        <ContentArea>
          <AppBadgesContainer>
            <AppBadge
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Apple App Store"
              onClick={onAppStoreClick}
            >
              <AppleStoreBadge />
            </AppBadge>
            <AppBadge
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Google Play Store"
              onClick={onGooglePlayClick}
            >
              <GooglePlayBadge />
            </AppBadge>
            <QRCodeWrapper>
              <img src={QRCode} alt="" width="88" height="88" />
            </QRCodeWrapper>
          </AppBadgesContainer>
        </ContentArea>

        <ImageArea>
          <AppImage src={BeyondAppImage} alt="Beyond app interface" />
        </ImageArea>
      </LayoutGrid>
    </Container>
  );
};
